<script lang="ts">
export default {
  name: 'MenuActions'
};
</script>

<script setup lang="ts">
import { Store } from 'vuex';
import { AppState } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { useRouter } from 'vue-router';
import { computed, inject, ref, watch } from 'vue';
import { ChevronsLeft, Users } from 'lucide-vue-next';
import { towerService } from '@/services/v2/TowerService';
import { Contributor } from '@/types/tower/v2/Contributor';

const store = inject<Store<AppState>>('store');
const router = useRouter();
const loading = ref<boolean>(true);
const showContributors = ref<boolean>(false);
const contributors = ref<Contributor[]>([]);

// ---------------
// watchers
// ---------------
watch(
  () => store?.state.towerCanvasState.currentTowerInstance?.towerId,
  async (newTowerId) => {
    if (newTowerId) {
      loading.value = true;
      contributors.value = await fetchTowerContributors(newTowerId);
      loading.value = false;
    }
  },
  { immediate: true }
);


// ---------------
// computed
// ---------------
const accountName = computed(() => {
  return store?.state.towerCanvasState.accountName ?? '';
});

const programName = computed(() => {
  return store?.state.towerCanvasState.programName ?? '';
});

const versionLabel = computed(() => {
  return store?.state.towerCanvasState.currentTowerInstance?.versionLabel ?? '';
});

const towerName = computed(() => {
  return store?.state.towerCanvasState.currentTowerInstance?.towerName ?? '';
});


// ---------------
// methods
// ---------------
function handleBack() {
  router.back();
}

function handleProgramClick() {
  store?.commit(StoreMutationType.SET_PROGRAMS_PANEL_STATE, {
    show: true,
    tab: 'versions'
  });
}

async function fetchTowerContributors(towerId: string) {
  loading.value = true;

  const contributors = await towerService.getTowerContributors(towerId);
  loading.value = false;
  return contributors;
}


</script>

<template>
  <div class="tw:flex tw:gap-2 tw:justify-self-start tw:justify-start tw:ml-2 tw:mt-2 tw:h-10">
    
    <div @click="handleBack" class="tw:flex-nowrap tw:bg-tower-alt
      tw:border tw:border-solid tw:border-tower-border
      tw:flex tw:items-center tw:justify-items-center
      tw:cursor-pointer tw:pointer-events-auto
      tw:border-box tw:rounded-md tw:shadow-lg
      tw:hover:bg-tower-hover tw:px-1 tw:py-1">
      
      <ChevronsLeft class="tw:scale-75" />
      <span class="tw:mr-2">Back</span>
    </div>

    <div v-if="contributors && contributors.length > 0" class="tw:relative tw:flex tw:flex-col tw:justify-center tw:ml-2 tw:pointer-events-auto tw:cursor-pointer">
      <Users
        :size="20"
        class="tw:text-gray-700 tw:self-end tw:pointer-events-auto"
        @mouseover="showContributors = true" 
        @mouseleave="showContributors = false"
      />
      <div v-show="showContributors" 
        class="tw:absolute tw:whitespace-nowrap tw:border tw:border-gray-300 tw:bg-white tw:shadow-md tw:px-4 tw:py-2 tw:rounded-md tw:z-10 tw:left-0 tw:top-7 tw:mt-1">
        <p class="tw:text-sm tw:font-bold tw:mb-1">Contributors</p>
        <ul class="tw:list-none tw:m-0 tw:p-0">
          <li v-for="(contributor, index) in contributors" :key="index" class="tw:text-sm">
            {{ contributor.name !== '' ? contributor.name : contributor.email }}
          </li>
        </ul>
      </div>
    </div>
    
    <div class="tw:flex tw:items-center tw:px-2 tw:cursor-pointer tw:pointer-events-auto tw:text-center">
      
      <div @click="handleProgramClick"
           class="tw:flex tw:flex-row tw:gap-3 tw:text-center tw:items-center" :title="versionLabel ?? 'Initial Version'">
        <span class="tw:font-bold">{{ accountName }}</span>
        <span class="tw:text-xs">{{ programName !== '' ? programName : towerName }}</span>
      </div>
    </div>
  </div>
</template>

<style>
</style>
